import React, { FC, useCallback, useMemo } from 'react';
import { styled } from '@mui/material';
import { Card } from '@mui/material';
import { COMMON_STOCK_VALUE, EQUITY_VALUE } from 'common/constants/dashboards';
import { LoadingSection } from 'components';
import { LineBarChart } from 'dashboard409a/components';
import { formatMD, VALUATION_TREND } from 'dashboard409a/utilities';
import { VALUATION_TREND_PLUGINS } from 'dashboard409a/utilities/optionCharts';
import { IValuationChartData, ValuationTrendProps } from './types';

const PREFIX = 'ValuationTrend';

const classes = {
  root: `${PREFIX}-root`,
};

const StyledCard = styled(Card)(() => ({
  [`&.${classes.root}`]: {
    marginBottom: '2%',
    padding: 'inherit',
  },
}));

const sortDataByDate = (data: IValuationChartData[]): IValuationChartData[] =>
  [...data].sort(
    (a, b) =>
      new Date(a.report_date.measurement_date.date).getTime() - new Date(b.report_date.measurement_date.date).getTime()
  );

const ValuationTrend: FC<ValuationTrendProps> = ({ data, isLoading }) => {
  const mapStockValues = useCallback(
    (dataParam: IValuationChartData[]): number[] => dataParam.map(item => +item.marketable_value_per_common_share),
    []
  );

  const mapWeightedValues = useCallback(
    (dataParam: IValuationChartData[]): number[] => dataParam.map(item => +item.weighted_equity_value),
    []
  );

  const { sortedData, stockValues, weightedValue } = useMemo(() => {
    if (!data) {
      return { sortedData: [], stockValues: [], weightedValue: [] };
    }

    const sorted = sortDataByDate(data);
    const stockVals = mapStockValues(sorted);
    const weightedVals = mapWeightedValues(sorted);

    return { sortedData: sorted, stockValues: stockVals, weightedValue: weightedVals };
  }, [data, mapStockValues, mapWeightedValues]);

  const parsedData = useMemo(
    () => ({
      labels: sortedData.map(item => formatMD({ date: item.report_date.measurement_date.date, format: 'MM/DD/YY' })),
      datasets: [
        {
          type: 'bar' as const,
          label: EQUITY_VALUE,
          yAxisID: 'equity',
          data: weightedValue,
          backgroundColor: '#80bef4',
          borderColor: '#80bef4',
        },
        {
          type: 'line' as const,
          label: COMMON_STOCK_VALUE,
          yAxisID: 'common',
          data: stockValues,
          backgroundColor: '#65C687',
          borderColor: '#65C687',
        },
      ],
    }),
    [sortedData, stockValues, weightedValue]
  );

  return isLoading || !sortedData.length ? (
    <LoadingSection />
  ) : (
    <StyledCard className={classes.root}>
      <LineBarChart data={parsedData} options={VALUATION_TREND} plugins={[VALUATION_TREND_PLUGINS]} />
    </StyledCard>
  );
};

export default ValuationTrend;
